import React, { useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  styled,
} from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import MDTypography from "components/MDTypography";

interface TableColumn {
  Header: string;
  accessor: string;
}

interface TableData<T> {
  columns: TableColumn[];
  rows: T[];
}

interface CustomTableProps<T> {
  total: number;
  loading: boolean;
  table: TableData<T>;
  pageSize: number;
  currentPage: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

const CustomDataTable = <T extends Record<string, unknown>>({
  total,
  loading,
  table,
  pageSize,
  currentPage,
  setPage,
  setPageSize,
}: CustomTableProps<T>) => {
  const handleChangePage = useCallback((_event: unknown, newPage: number) => setPage(newPage), [setPage]);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPageSize(parseInt(event.target.value, 10));
      setPage(0);
    },
    [setPage, setPageSize]
  );

  return (
    <TableContainer>
      <StyledTable>
        <TableHead>
          <TableRow>
            {table.columns.map((column) => (
              <StyledTableCell key={column.accessor}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  {column.Header}
                </MDTypography>
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={table.columns.length} align="center">
                <CircularProgress size={20} />
              </TableCell>
            </TableRow>
          ) : table.rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={table.columns.length} align="center">
                <MDTypography variant="caption" color="text">
                  No data
                </MDTypography>
              </TableCell>
            </TableRow>
          ) : (
            table.rows.map((row, index) => (
              <TableRow key={row.FX_UNIQUE_ID ? String(row.FX_UNIQUE_ID) : `row-${index}`}>
                {table.columns.map((column) => (
                  <TableCell key={`${index}-${column.accessor}`}>
                    <MDTypography variant="caption" color="text">
                      {String(row[column.accessor] ?? "-")}
                    </MDTypography>
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </StyledTable>
      <TablePagination
        rowsPerPageOptions={[5, 10]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={currentPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
            fontSize: "0.75rem",
            color: colors.text.main,
          },
          ".MuiTablePagination-select": {
            fontSize: "0.75rem",
            color: colors.text.main,
          },
          ".MuiTablePagination-actions button": {
            color: colors.text.main,
          },
        }}
      />
    </TableContainer>
  );
};

const TableContainer = styled("div")`
  overflow-x: auto;
  background: ${colors.background.default};
  border-radius: 4px;
`;

const StyledTable = styled(Table)`
  min-width: 400px;
`;

const StyledTableCell = styled(TableCell)`
  background: ${colors.background.card};
  color: ${colors.text.main};
  border-bottom: 1px solid ${colors.grey[800]};
  padding: 8px;
  position: sticky;
  top: 0;
`;

export default CustomDataTable;
